import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { API } from "../../../cache";
import AppSidebar, { SB_W, SB_W_COLLAPSED } from "../../../AppSidebar";
import "../../../V2SideDrawer.css";
import "../styles/UsersViewV2.css";
import UserCreateDrawer from "../components/UserCreateDrawer";
import UserInviteGuestDrawer from "../components/UserInviteGuestDrawer";
import UserEditDrawer from "../components/UserEditDrawer";
import { emailAuthApi } from "../../auth/api/emailAuthApi";
import { ChoiceFilterPopover, FilterTriggerButton, PageToolbarSearch } from "../../common/components";
import { listFilterTemplates } from "../../common/services/filterTemplatesApi";

const ROLE_OPTIONS      = ["", "Admin", "AM", "Project Lead", "Developer", "Guest", "External"];
const USER_TYPE_OPTIONS = ["", "Internal", "External", "Guest"];

// flex-based columns — no fixed pixel widths
const COLS = [
  { key: "display_name", label: "Name",      flex: 2 },
  { key: "user_id",      label: "User ID",   flex: 1.1 },
  { key: "user_name",    label: "Email",      flex: 3.5, readonly: true },
  { key: "role",         label: "Role",       flex: 1,   type: "select", options: ROLE_OPTIONS },
  { key: "reporting_manager_name", label: "Reporting Manager", flex: 1.6, type: "manager_select" },
  { key: "mobile_no",    label: "Mobile",     flex: 1 },
  { key: "location",     label: "Location",   flex: 1 },
  { key: "is_active",    label: "Status",     flex: 0.8, type: "toggle" },
  { key: "invitation_status", label: "Invitation", flex: 1.2, readonly: true },
  { key: "last_login_method", label: "Last sign-in", flex: 1.4, type: "last_signin", readonly: true },
];

const LOGIN_METHOD_LABEL = { microsoft: "Microsoft", email: "Email" };

function fmtSignInDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
}

const TEAM_COLS = [
  { key: "team_name",      label: "Team Name",   flex: 1.5 },
  { key: "description",    label: "Description", flex: 2.5 },
  { key: "team_lead_name", label: "Team Lead",   flex: 1.5 },
];

// Actions column widths (fixed, known button count)
const TEAM_ACT_W  = 210; // Members + Edit + Delete / Save+Cancel

function normalizeUserType(value) {
  return String(value || "").trim().toLowerCase();
}

function forceExternalClientTeamForType(userType) {
  const t = normalizeUserType(userType);
  return t === "external" || t === "admin";
}

async function apiFetch(url, opts = {}) {
  const res = await fetch(url, { credentials: "include", ...opts });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || `HTTP ${res.status}`);
  }
  if (res.status === 204) return null;
  return res.json();
}

// ── Teams Section ─────────────────────────────────────────────────────────────
function TeamsSection({ teams, loading, users, showToast, onRefresh }) {
  const [addForm,        setAddForm]        = useState(null);
  const [editId,         setEditId]         = useState(null);
  const [editDraft,      setEditDraft]      = useState({});
  const [saving,         setSaving]         = useState(false);
  const [membersTeamId,  setMembersTeamId]  = useState(null);
  const [membersList,    setMembersList]    = useState([]);
  const [membersLoading, setMembersLoading] = useState(false);

  const openMembers = async (teamId) => {
    if (membersTeamId === teamId) { setMembersTeamId(null); return; }
    setMembersTeamId(teamId);
    setMembersLoading(true);
    try {
      const data = await apiFetch(`${API}/teams/${teamId}/members`);
      setMembersList(Array.isArray(data) ? data : []);
    } catch { showToast("Failed to load members", "warn"); setMembersList([]); }
    setMembersLoading(false);
  };

  const removeMember = async (teamId, userId, displayName) => {
    if (!window.confirm(`Remove ${displayName} from this team?`)) return;
    try {
      await apiFetch(`${API}/teams/${teamId}/members/${userId}`, { method: "DELETE" });
      setMembersList(prev => prev.filter(m => m.id !== userId));
      onRefresh();
      showToast(`${displayName} removed`);
    } catch (e) { showToast(e.message || "Failed to remove member", "warn"); }
  };

  const startEdit  = (t) => { setEditId(t.id); setEditDraft({ ...t }); };
  const cancelEdit = () => { setEditId(null); setEditDraft({}); };

  const saveEdit = async () => {
    setSaving(true);
    try {
      await apiFetch(`${API}/teams/${editDraft.id}`, {
        method: "PUT", headers: { "Content-Type": "application/json" },
        body: JSON.stringify(editDraft),
      });
      showToast("Team updated"); setEditId(null); onRefresh();
    } catch (e) { showToast(e.message || "Failed to update team", "warn"); }
    setSaving(false);
  };

  const create = async () => {
    if (!addForm.team_name?.trim()) { showToast("Team name is required", "warn"); return; }
    setSaving(true);
    try {
      await apiFetch(`${API}/teams`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify(addForm),
      });
      showToast("Team created"); setAddForm(null); onRefresh();
    } catch (e) { showToast(e.message || "Failed to create team", "warn"); }
    setSaving(false);
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this team?")) return;
    try {
      await apiFetch(`${API}/teams/${id}`, { method: "DELETE" });
      showToast("Team deleted"); onRefresh();
    } catch (e) { showToast(e.message || "Failed to delete team", "warn"); }
  };

  const cs  = (col) => ({ flex: col.flex, minWidth: 0, overflow: "hidden" });
  const aw  = { width: TEAM_ACT_W, minWidth: TEAM_ACT_W };

  return (
    <div className="uv-table-wrap">
      <div className="uv-col-row">
        <div className="uv-col-num">#</div>
        {TEAM_COLS.map(col => (
          <div key={col.key} className="uv-col-cell" style={cs(col)}>{col.label}</div>
        ))}
        <div className="uv-col-cell uv-col-actions" style={aw}>Actions</div>
      </div>

      {loading && <div className="uv-state">Loading…</div>}
      {!loading && teams.length === 0 && !addForm && <div className="uv-state">No teams yet.</div>}

      {!loading && teams.map((t, idx) => {
        const isEditing = editId === t.id;
        return (
          <React.Fragment key={t.id}>
            <div className={`uv-row${isEditing ? " uv-row--editing" : ""}`}>
              <div className="uv-row-num">{idx + 1}</div>
              {isEditing ? (
                <>
                  <div className="uv-cell uv-cell--edit" style={cs(TEAM_COLS[0])}>
                    <input className="uv-input" value={editDraft.team_name ?? ""} onChange={e => setEditDraft(d => ({ ...d, team_name: e.target.value }))} autoFocus />
                  </div>
                  <div className="uv-cell uv-cell--edit" style={cs(TEAM_COLS[1])}>
                    <input className="uv-input" value={editDraft.description ?? ""} onChange={e => setEditDraft(d => ({ ...d, description: e.target.value }))} />
                  </div>
                  <div className="uv-cell uv-cell--edit" style={cs(TEAM_COLS[2])}>
                    <select className="uv-select" value={editDraft.team_lead_id ?? ""} onChange={e => setEditDraft(d => ({ ...d, team_lead_id: e.target.value || null }))}>
                      <option value="">— No lead —</option>
                      {users.map(u => <option key={u.id} value={u.id}>{u.display_name}</option>)}
                    </select>
                  </div>
                </>
              ) : (
                TEAM_COLS.map(col => {
                  const val = t[col.key];
                  return (
                    <div key={col.key} className="uv-cell" style={cs(col)}>
                      {val ? <span className="uv-cell-text" title={val}>{val}</span> : <span className="uv-empty">—</span>}
                    </div>
                  );
                })
              )}
              <div className="uv-cell uv-cell--actions" style={aw}>
                {isEditing ? (
                  <>
                    <button className="uv-btn uv-btn--save" onClick={saveEdit} disabled={saving}>{saving ? "…" : "Save"}</button>
                    <button className="uv-btn uv-btn--cancel" onClick={cancelEdit} disabled={saving}>Cancel</button>
                  </>
                ) : (
                  <>
                    <button className="uv-btn uv-btn--members" onClick={() => openMembers(t.id)}>
                      {membersTeamId === t.id ? "Hide" : "Members"}
                    </button>
                    <button className="uv-btn uv-btn--edit" onClick={() => startEdit(t)}>Edit</button>
                    <button className="uv-btn uv-btn--cancel" onClick={() => remove(t.id)}>Delete</button>
                  </>
                )}
              </div>
            </div>

            {membersTeamId === t.id && (
              <div className="uv-members-panel">
                {membersLoading ? (
                  <span className="uv-members-loading">Loading…</span>
                ) : membersList.length === 0 ? (
                  <span className="uv-members-empty">No members assigned to this team.</span>
                ) : (
                  <ul className="uv-members-list">
                    {membersList.map(m => (
                      <li key={m.id} className="uv-members-item">
                        <span className="uv-members-name">{m.display_name}</span>
                        {m.role && <span className="uv-members-role">{m.role}</span>}
                        <button className="uv-btn uv-btn--remove" onClick={() => removeMember(t.id, m.id, m.display_name)}>Remove</button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </React.Fragment>
        );
      })}

      {addForm && (
        <div className="uv-row uv-row--editing">
          <div className="uv-row-num">—</div>
          <div className="uv-cell uv-cell--edit" style={cs(TEAM_COLS[0])}>
            <input className="uv-input" value={addForm.team_name} onChange={e => setAddForm(f => ({ ...f, team_name: e.target.value }))} placeholder="Team name *" autoFocus />
          </div>
          <div className="uv-cell uv-cell--edit" style={cs(TEAM_COLS[1])}>
            <input className="uv-input" value={addForm.description} onChange={e => setAddForm(f => ({ ...f, description: e.target.value }))} placeholder="Description (optional)" />
          </div>
          <div className="uv-cell uv-cell--edit" style={cs(TEAM_COLS[2])}>
            <select className="uv-select" value={addForm.team_lead_id || ""} onChange={e => setAddForm(f => ({ ...f, team_lead_id: e.target.value || null }))}>
              <option value="">— No lead —</option>
              {users.map(u => <option key={u.id} value={u.id}>{u.display_name}</option>)}
            </select>
          </div>
          <div className="uv-cell uv-cell--actions" style={aw}>
            <button className="uv-btn uv-btn--save" onClick={create} disabled={saving}>{saving ? "…" : "Save"}</button>
            <button className="uv-btn uv-btn--cancel" onClick={() => setAddForm(null)}>Cancel</button>
          </div>
        </div>
      )}

      {!addForm && !loading && (
        <div style={{ padding: "8px 12px" }}>
          <button className="uv-btn uv-btn--edit" onClick={() => setAddForm({ team_name: "", description: "", team_lead_id: null })}>
            + Add Team
          </button>
        </div>
      )}
    </div>
  );
}

// ── Main View ─────────────────────────────────────────────────────────────────
export default function UsersViewV2Page({ showToast, user }) {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(
    () => localStorage.getItem("sb-collapsed") === "true"
  );
  const [activeTab,    setActiveTab]    = useState("internal");
  const [users,        setUsers]        = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [editId,       setEditId]       = useState(null);
  const [editDrawerOpen, setEditDrawerOpen] = useState(false);
  const [editDraft,    setEditDraft]    = useState({});
  const [editTeamId,   setEditTeamId]   = useState("");
  const [saving,       setSaving]       = useState(false);
  const [search,       setSearch]       = useState("");
  const [filterMode,   setFilterMode]   = useState("all");
  const [filterOpen,   setFilterOpen]   = useState(false);
  const [appliedFilters, setAppliedFilters] = useState({});

  useEffect(() => {
    listFilterTemplates("users").then((templates) => {
      const def = (templates || []).find((t) => t.is_default);
      if (def) { setAppliedFilters(def.filter_def || {}); setFilterMode(def.filter_mode || "all"); }
    }).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const searchRef = useRef(null);

  const [teams,           setTeams]           = useState([]);
  const [teamsLoading,    setTeamsLoading]    = useState(true);
  const [allProjects,     setAllProjects]     = useState([]);
  const [editProjectIds,  setEditProjectIds]  = useState([]);
  const [allCustomers,    setAllCustomers]    = useState([]);
  const [editCustomerIds, setEditCustomerIds] = useState([]);
  const [createDrawerOpen, setCreateDrawerOpen] = useState(false);
  const [creatingUser, setCreatingUser] = useState(false);
  const [inviteDrawerOpen, setInviteDrawerOpen] = useState(false);
  const [inviteUserContext, setInviteUserContext] = useState(null);
  const [customerGroups, setCustomerGroups] = useState([]);
  const [loadingCustomerGroups, setLoadingCustomerGroups] = useState(false);
  const [invitingGuest, setInvitingGuest] = useState(false);

  const isAdmin = ["admin", "super admin"].includes(String(user?.db_role || "").toLowerCase());
  const clientTeamId = useMemo(
    () =>
      (teams || []).find((t) => String(t.team_name || "").trim().toLowerCase() === "client team")?.id || "",
    [teams]
  );

  const tabUsers = useMemo(() => {
    if (activeTab === "internal") return users.filter(u => normalizeUserType(u.user_type) === "internal");
    if (activeTab === "external") return users.filter(u => normalizeUserType(u.user_type) !== "internal");
    return users;
  }, [users, activeTab]);

  const userFilterFields = useMemo(() => {
    const uniq = (vals) => [...new Set(vals.filter(Boolean).map((v) => String(v).trim()).filter(Boolean))];
    const role = uniq(tabUsers.map((u) => u.role)).sort((a, b) => a.localeCompare(b));
    const invitation = uniq(tabUsers.map((u) => u.invitation_status)).sort((a, b) => a.localeCompare(b));
    const managers = uniq(tabUsers.map((u) => u.reporting_manager_name)).sort((a, b) => a.localeCompare(b));

    return [
      { key: "role", label: "Role", options: role.map((v) => ({ value: v, label: v })) },
      {
        key: "status",
        label: "Status",
        options: [
          { value: "active", label: "Active" },
          { value: "inactive", label: "Inactive" },
        ],
      },
      { key: "invitation_status", label: "Invitation", options: invitation.map((v) => ({ value: v, label: v })) },
      {
        key: "last_login_method",
        label: "Last sign-in",
        options: [
          { value: "microsoft", label: "Microsoft" },
          { value: "email", label: "Email" },
          { value: "never", label: "Never signed in" },
        ],
      },
      { key: "reporting_manager_name", label: "Reporting Manager", options: managers.map((v) => ({ value: v, label: v })) },
      {
        key: "team_id",
        label: "Team",
        options: (teams || [])
          .map((t) => ({ value: String(t.id), label: t.team_name }))
          .filter((t) => t.value && t.label)
          .sort((a, b) => a.label.localeCompare(b.label)),
      },
      {
        key: "customer_id",
        label: "Customer",
        options: (allCustomers || [])
          .map((c) => ({ value: String(c.id), label: c.customer_name }))
          .filter((c) => c.value && c.label)
          .sort((a, b) => a.label.localeCompare(b.label)),
      },
      {
        key: "project_id",
        label: "Project",
        options: (allProjects || [])
          .map((p) => ({ value: String(p.id), label: p.project_name }))
          .filter((p) => p.value && p.label)
          .sort((a, b) => a.label.localeCompare(b.label)),
      },
    ];
  }, [tabUsers, teams, allCustomers, allProjects]);

  const activeFilterCount = useMemo(
    () => Object.values(appliedFilters || {}).reduce((n, f) => n + ((f?.values || []).length || 0), 0),
    [appliedFilters]
  );

  const usersByAdvancedFilters = useMemo(() => {
    const entries = Object.entries(appliedFilters || {}).filter(([, f]) => Array.isArray(f?.values) && f.values.length > 0);
    if (!entries.length) return tabUsers;

    const fieldValues = (u, key) => {
      if (key === "status") return [u.is_active ? "active" : "inactive"];
      if (key === "last_login_method") return [u.last_login_method || "never"];
      if (key === "team_id") return (u.teams || []).map((t) => String(t.id));
      if (key === "customer_id") return (u.customers || []).map((c) => String(c.id));
      if (key === "project_id") return (u.projects || []).map((p) => String(p.id));
      const v = u[key];
      return v === null || v === undefined || String(v).trim() === "" ? [] : [String(v).trim()];
    };

    return tabUsers.filter((u) => {
      const matches = entries.map(([key, rule]) => {
        const rowVals = fieldValues(u, key);
        const hit = (rule.values || []).some((v) => rowVals.includes(String(v)));
        return rule.op === "is_not" ? !hit : hit;
      });
      return filterMode === "any" ? matches.some(Boolean) : matches.every(Boolean);
    });
  }, [tabUsers, appliedFilters, filterMode]);

  const q = search.trim().toLowerCase();
  const filteredUsers = q
    ? usersByAdvancedFilters.filter(u =>
        (u.display_name ?? "").toLowerCase().includes(q) ||
        (u.user_id      ?? "").toLowerCase().includes(q) ||
        (u.user_name    ?? "").toLowerCase().includes(q) ||
        (u.role         ?? "").toLowerCase().includes(q) ||
        (u.reporting_manager_name ?? "").toLowerCase().includes(q) ||
        (u.user_type    ?? "").toLowerCase().includes(q) ||
        (u.mobile_no    ?? "").toLowerCase().includes(q) ||
        (u.location     ?? "").toLowerCase().includes(q) ||
        (u.invitation_status ?? "").toLowerCase().includes(q)
      )
    : usersByAdvancedFilters;

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && String(e.key).toLowerCase() === "k") {
        e.preventDefault();
        searchRef.current?.focus();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/users`, { credentials: "include" });
      const data = res.ok ? await res.json() : [];
      setUsers(Array.isArray(data) ? data : (data?.users ?? data?.data ?? []));
    } catch {
      showToast("Failed to load users", "warn");
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const fetchTeams = useCallback(async () => {
    setTeamsLoading(true);
    try { setTeams(await apiFetch(`${API}/teams`)); }
    catch { showToast("Failed to load teams", "warn"); }
    setTeamsLoading(false);
  }, [showToast]);

  useEffect(() => { fetchTeams(); }, [fetchTeams]);

  useEffect(() => {
    fetch(`${API}/customers-projects?active_only=true`, { credentials: "include" })
      .then(r => r.ok ? r.json() : [])
      .then(data => {
        const list = Array.isArray(data) ? data : [];
        setAllCustomers(list.map(c => ({ id: c.customer_id, customer_name: c.customer_name })));
        const flat = list.flatMap(c => (c.projects || []).map(p => ({
          id:            p.project_id,
          project_name:  p.project_name,
          customer_name: c.customer_name,
        })));
        setAllProjects(flat);
      })
      .catch(() => {});
  }, []);

  const startEdit = (u) => {
    setEditId(u.id);
    const mustForce = forceExternalClientTeamForType(u.user_type);
    setEditDraft({
      ...u,
      role: mustForce ? "External" : (u.role || ""),
    });
    setEditTeamId(mustForce ? (clientTeamId || "") : ((u.teams || [])[0]?.id || ""));
    setEditProjectIds((u.projects || []).map(p => p.id));
    setEditCustomerIds((u.customers || []).map(c => c.id));
    setEditDrawerOpen(true);
  };

  const cancelEdit = () => {
    setEditDrawerOpen(false);
    setEditId(null);
    setEditDraft({});
    setEditTeamId("");
    setEditProjectIds([]);
    setEditCustomerIds([]);
  };

  const saveEdit = async () => {
    setSaving(true);
    try {
      const mustForce = forceExternalClientTeamForType(editDraft.user_type);
      const effectiveDraft = {
        ...editDraft,
        role: mustForce ? "External" : editDraft.role,
      };
      const effectiveTeamId = mustForce ? (clientTeamId || "") : editTeamId;
      if (mustForce && !effectiveTeamId) {
        throw new Error("Team 'Client Team' is not configured");
      }

      const res = await fetch(`${API}/users/${editDraft.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(effectiveDraft),
      });
      if (!res.ok) throw new Error();

      await fetch(`${API}/users/${editDraft.id}/teams`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ team_ids: effectiveTeamId ? [effectiveTeamId] : [] }),
      });

      await fetch(`${API}/users/${editDraft.id}/projects`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ project_ids: editProjectIds }),
      });

      await fetch(`${API}/users/${editDraft.id}/customers`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ customer_ids: editCustomerIds }),
      });

      const assignedTeams = teams
        .filter((t) => t.id === effectiveTeamId)
        .map((t) => ({ id: t.id, team_name: t.team_name }));
      const assignedCustomers = allCustomers.filter(c => editCustomerIds.includes(c.id)).map(c => ({ id: c.id, customer_name: c.customer_name }));
      const managerName = users.find((x) => x.id === effectiveDraft.reporting_manager_id)?.display_name || null;

      // Auto-expand projects list with all projects from mapped customers
      const customerProjectIds = new Set(editProjectIds);
      allProjects
        .filter(p => editCustomerIds.some(cid => {
          const customer = allCustomers.find(c => c.id === cid);
          return customer && p.customer_name === customer.customer_name;
        }))
        .forEach(p => customerProjectIds.add(p.id));
      const mergedProjects = allProjects
        .filter(p => customerProjectIds.has(p.id))
        .map(p => ({ id: p.id, project_name: p.project_name, customer_name: p.customer_name }));

      setUsers(prev => prev.map(u =>
        u.id === effectiveDraft.id
          ? {
              ...effectiveDraft,
              reporting_manager_name: managerName,
              teams: assignedTeams,
              projects: mergedProjects,
              customers: assignedCustomers,
            }
          : u
      ));
      showToast("User saved ✓");
      cancelEdit();
    } catch {
      showToast("Failed to save user", "warn");
    }
    setSaving(false);
  };

  const createUserFromDrawer = async (payload) => {
    setCreatingUser(true);
    try {
      const mustForce = forceExternalClientTeamForType(payload.user_type);
      const effectiveTeamId = mustForce ? (clientTeamId || "") : payload.team_id;
      if (mustForce && !effectiveTeamId) {
        throw new Error("Team 'Client Team' is not configured");
      }

      const created = await apiFetch(`${API}/users`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          user_id: payload.user_id || "",
          display_name: payload.display_name || "",
          user_name: payload.user_name || "",
          user_type: payload.user_type || "",
          role: mustForce ? "External" : (payload.role || ""),
          mobile_no: payload.mobile_no || "",
          is_active: true,
          reporting_manager_id: payload.reporting_manager_id || null,
          email_login_enabled: !!payload.email_login_enabled,
        }),
      });
      const newUserId = created?.id;
      if (!newUserId) throw new Error("User created but id not returned");

      await apiFetch(`${API}/users/${newUserId}/teams`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ team_ids: effectiveTeamId ? [effectiveTeamId] : [] }),
      });

      await apiFetch(`${API}/users/${newUserId}/customers`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ customer_ids: payload.customer_ids || [] }),
      });

      await apiFetch(`${API}/users/${newUserId}/projects`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ project_ids: payload.project_ids || [] }),
      });

      // External users get the invitation that matches their sign-in method.
      let inviteNote = "";
      let inviteFailed = false;
      if (payload.invite?.method === "email") {
        try {
          await emailAuthApi.sendInvite(newUserId);
          inviteNote = " · email setup invite sent";
        } catch (inviteErr) {
          inviteFailed = true;
          inviteNote = ` · email invite not sent: ${inviteErr.message}`;
        }
      } else if (payload.invite?.method === "microsoft") {
        try {
          await apiFetch(`${API}/api/admin/invite-user`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              email: payload.user_name,
              displayName: payload.display_name,
              groupId: payload.invite.groupId,
              groupName: payload.invite.groupName,
            }),
          });
          inviteNote = " · Microsoft invitation sent";
        } catch (inviteErr) {
          inviteFailed = true;
          inviteNote = ` · Microsoft invitation not sent: ${inviteErr.message}`;
        }
      }

      await fetchUsers();
      showToast(`User created ✓${inviteNote}`, inviteFailed ? "warn" : "success");
      setCreateDrawerOpen(false);
    } catch (e) {
      showToast(e.message || "Failed to create user", "warn");
    } finally {
      setCreatingUser(false);
    }
  };

  const fetchCustomerGroups = useCallback(async ({ refresh = false } = {}) => {
    setLoadingCustomerGroups(true);
    try {
      const url = refresh ? `${API}/api/admin/customer-groups?refresh=true` : `${API}/api/admin/customer-groups`;
      const data = await apiFetch(url);
      const list = Array.isArray(data) ? data : (data?.groups || []);
      setCustomerGroups(Array.isArray(list) ? list : []);
    } catch (e) {
      setCustomerGroups([]);
      showToast(e.message || "Failed to load customer groups", "warn");
    } finally {
      setLoadingCustomerGroups(false);
    }
  }, [showToast]);

  const openInviteDrawer = async (targetUser = null) => {
    setEditDrawerOpen(false);
    setInviteUserContext(targetUser);
    setInviteDrawerOpen(true);
    await fetchCustomerGroups();
  };

  const inviteGuestUser = async (payload) => {
    setInvitingGuest(true);
    try {
      const res = await apiFetch(`${API}/api/admin/invite-user`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const groupName = res?.data?.groupName || payload.groupName || "selected group";
      const email = res?.data?.email || payload.email;
      showToast(`Invite processed for ${email} in ${groupName}`);
      setInviteDrawerOpen(false);
      setInviteUserContext(null);
      fetchUsers();
    } catch (e) {
      showToast(e.message || "Failed to invite user", "warn");
    } finally {
      setInvitingGuest(false);
    }
  };

  return (
    <div
      className="uv2-root"
      style={{ gridTemplateColumns: sidebarCollapsed ? `${SB_W_COLLAPSED} 1fr` : `${SB_W} 1fr` }}
    >
      <AppSidebar
        user={user}
        collapsed={sidebarCollapsed}
        onCollapse={() => setSidebarCollapsed((c) => {
          const next = !c;
          localStorage.setItem("sb-collapsed", String(next));
          return next;
        })}
      />

      <div className="uv2-shell">
        <div className="page-head">
          <h1>
            Users <em>{users.length} records</em>
          </h1>
          <div className="page-head-actions">
            {isAdmin && (
              <button
                className="v2-btn-primary"
                type="button"
                onClick={() => setCreateDrawerOpen(true)}
                disabled={creatingUser || loading}
                title="Create new user"
              >
                + New User
              </button>
            )}
          </div>
        </div>

      {/* ── Tab bar ── */}
      <div className="page-toolbar">
        <div className="page-tabs">
          <button className={`page-tab${activeTab === "internal" ? " page-tab--active" : ""}`} onClick={() => { setActiveTab("internal"); setAppliedFilters({}); setSearch(""); }}>
            Internal Users
            <span className="page-tab-count">{users.filter(u => normalizeUserType(u.user_type) === "internal").length}</span>
          </button>
          <button className={`page-tab${activeTab === "external" ? " page-tab--active" : ""}`} onClick={() => { setActiveTab("external"); setAppliedFilters({}); setSearch(""); }}>
            External Users
            <span className="page-tab-count">{users.filter(u => normalizeUserType(u.user_type) !== "internal").length}</span>
          </button>
          <button className={`page-tab${activeTab === "teams" ? " page-tab--active" : ""}`} onClick={() => setActiveTab("teams")}>
            Teams
            {teams.length > 0 && <span className="page-tab-count">{teams.length}</span>}
          </button>
        </div>
        {(activeTab === "internal" || activeTab === "external") && (
          <div className="page-toolbar-right">
            <PageToolbarSearch
              inputRef={searchRef}
              value={search}
              onChange={setSearch}
              placeholder="Search all columns..."
              shortcutLabel="⌘K"
            />
            <div className="cmn-filter-wrap">
              <FilterTriggerButton
                activeCount={activeFilterCount}
                onClick={() => setFilterOpen((o) => !o)}
                onClear={() => {
                  setAppliedFilters({});
                  setFilterMode("all");
                }}
              />
              <ChoiceFilterPopover
                open={filterOpen}
                title="Filter Users"
                fields={userFilterFields}
                appliedFilters={appliedFilters}
                filterMode={filterMode}
                module="users"
                onApply={(draft, mode) => { setAppliedFilters(draft); setFilterMode(mode); }}
                onClose={() => setFilterOpen(false)}
              />
            </div>
            <span className="toolbar__count">{filteredUsers.length} of {tabUsers.length} users</span>
          </div>
        )}
      </div>

      <div className="uv2-scroll">
      {/* ── Users tabs ── */}
      {(activeTab === "internal" || activeTab === "external") && (
        <>
          <div className="uv-table-wrap">
            <div className="uv-col-row">
              <div className="uv-col-num">#</div>
              {COLS.map(col => (
                <div key={col.key} className="uv-col-cell" style={{ flex: col.flex, minWidth: 0 }}>{col.label}</div>
              ))}
              <div className="uv-col-cell" style={{ flex: 1.5, minWidth: 0 }}>Customers</div>
              <div className="uv-col-cell" style={{ flex: 1.5, minWidth: 0 }}>Teams</div>
              <div className="uv-col-cell" style={{ flex: 1.5, minWidth: 0 }}>Projects</div>
            </div>

            {loading && <div className="uv-state">Loading…</div>}
            {!loading && filteredUsers.length === 0 && (
              <div className="uv-state">{q ? "No users match your search." : "No users found."}</div>
            )}

            {!loading && filteredUsers.map((u, idx) => {
              return (
                <div
                  key={u.id ?? idx}
                  className={`uv-row uv-row--clickable${editId === u.id && editDrawerOpen ? " uv-row--editing" : ""}`}
                  role="button"
                  tabIndex={0}
                  onClick={() => startEdit(u)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      startEdit(u);
                    }
                  }}
                  title="Click to edit"
                >
                  <div className="uv-row-num">{idx + 1}</div>

                  {COLS.map(col => {
                    if (col.type === "last_signin") {
                      const method = LOGIN_METHOD_LABEL[u.last_login_method];
                      const when = fmtSignInDate(u.last_login_method === "email" ? u.last_email_login_at : u.last_msal_login_at);
                      return (
                        <div key={col.key} className="uv-cell" style={{ flex: col.flex, minWidth: 0 }}>
                          {method
                            ? <span className="uv-cell-text" title={`Last signed in with ${method}${when ? ` on ${when}` : ""}`}>
                                <span className={`uv-signin-pill uv-signin-pill--${u.last_login_method}`}>{method}</span>{when && ` ${when}`}
                              </span>
                            : <span className="uv-empty">Never</span>}
                        </div>
                      );
                    }
                    if (col.type === "toggle") {
                      return (
                        <div key={col.key} className="uv-cell" style={{ flex: col.flex, minWidth: 0 }}>
                          <span className={`uv-badge uv-badge--${u.is_active ? "active" : "inactive"}`}>
                            {u.is_active ? "Active" : "Inactive"}
                          </span>
                        </div>
                      );
                    }
                    const val = u[col.key];
                    return (
                      <div key={col.key} className="uv-cell" style={{ flex: col.flex, minWidth: 0 }}>
                        {val ? <span className="uv-cell-text" title={val}>{val}</span> : <span className="uv-empty">—</span>}
                      </div>
                    );
                  })}

                  <div className="uv-cell" style={{ flex: 1.5, minWidth: 0 }}>
                    {activeTab === "external"
                      ? ((u.customers || [])[0]?.customer_name
                          ? <span className="uv-cell-text" title={u.customers[0].customer_name}>{u.customers[0].customer_name}</span>
                          : <span className="uv-empty">—</span>)
                      : <span className="uv-count-pill">{(u.customers || []).length}</span>
                    }
                  </div>

                  <div className="uv-cell" style={{ flex: 1.5, minWidth: 0 }}>
                    {(u.teams || []).length === 0
                      ? <span className="uv-empty">—</span>
                      : (u.teams || []).map(t => <span key={t.id} className="uv-team-pill">{t.team_name}</span>)
                    }
                  </div>

                  <div className="uv-cell" style={{ flex: 1.5, minWidth: 0 }}>
                    <span className="uv-count-pill">{(u.projects || []).length}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* ── Teams tab ── */}
      {activeTab === "teams" && (
        <TeamsSection
          teams={teams}
          loading={teamsLoading}
          users={users}
          showToast={showToast}
          onRefresh={fetchTeams}
        />
      )}

      <UserCreateDrawer
        open={createDrawerOpen}
        onClose={() => { if (!creatingUser) setCreateDrawerOpen(false); }}
        onCreate={createUserFromDrawer}
        creating={creatingUser}
        users={users}
        teams={teams}
        customers={allCustomers}
        projects={allProjects}
        actorRole={user?.db_role}
        customerGroups={customerGroups}
        loadingCustomerGroups={loadingCustomerGroups}
        onLoadCustomerGroups={fetchCustomerGroups}
      />
      <UserInviteGuestDrawer
        open={inviteDrawerOpen}
        onClose={() => {
          if (!invitingGuest) {
            setInviteDrawerOpen(false);
            setInviteUserContext(null);
          }
        }}
        onInvite={inviteGuestUser}
        onRefreshGroups={() => fetchCustomerGroups({ refresh: true })}
        inviting={invitingGuest}
        groups={customerGroups}
        groupsLoading={loadingCustomerGroups}
        initialEmail={inviteUserContext?.user_name || ""}
        initialDisplayName={inviteUserContext?.display_name || ""}
        lockIdentity={!!inviteUserContext}
      />
      <UserEditDrawer
        open={editDrawerOpen}
        onClose={cancelEdit}
        onSave={saveEdit}
        saving={saving}
        draft={editDraft}
        onDraftChange={(key, value) => setEditDraft((d) => {
          const next = { ...d, [key]: value };
          if (forceExternalClientTeamForType(next.user_type)) {
            next.role = "External";
            if (clientTeamId) {
              setEditTeamId(clientTeamId);
            }
          }
          return next;
        })}
        users={users}
        teams={teams}
        customers={allCustomers}
        projects={allProjects}
        teamId={editTeamId}
        onTeamIdChange={(nextTeamId) => {
          if (forceExternalClientTeamForType(editDraft.user_type)) {
            setEditTeamId(clientTeamId || "");
            return;
          }
          setEditTeamId(nextTeamId);
        }}
        customerIds={editCustomerIds}
        onCustomerIdsChange={setEditCustomerIds}
        projectIds={editProjectIds}
        onProjectIdsChange={setEditProjectIds}
        canInvite={String(editDraft?.user_type || "").toLowerCase() === "external"}
        inviteBusy={invitingGuest || loadingCustomerGroups}
        onInvite={() => openInviteDrawer(editDraft)}
        isAdmin={isAdmin}
        actorRole={user?.db_role}
      />
      </div>
    </div>
    </div>
  );
}
